// src/prototype/config/opportunityBadge.ts
// ============================================================
// OPPORTUNITY BADGE — SINGLE SOURCE OF TRUTH (quadrant rule)
// The Quick Win badge is derived from the Priority Matrix QUADRANT, not from effort/time:
//   Financial Value x Ease of Implementation, split at 50 (see quadrantFromAxes). Oct 21 v3.
// The old "Low implementation effort AND time-to-first-value <= 6 months" rule is RETIRED; it is kept
// only as a last-resort fallback for callers that provide neither a use-case id nor value/feas axes.
// Every surface that badges an opportunity imports from here. (Wave 2 · P24 → Option D re-root)
// ============================================================

import {
  OPP_TO_UC,
  quadrantForUseCase,
  quadrantFromAxes,
  scoreForUseCase,
  type Quadrant,
} from '../data/adapters/stage300FeaturedContent';

export interface BadgeInput {
  id?: string;                    // preferred: resolves the use case → real quadrant
  bvAxis?: number;                // fallback: value axis (0-100)
  ieAxis?: number;                // fallback: feasibility axis (0-100)
  implementationEffort?: string;  // legacy last-resort only
  timeToValue?: string;           // legacy last-resort only
}

export type OpportunityBadge = 'Quick Win' | 'Big Bet' | 'Incremental' | 'Moderate' | 'Complex';

// Leading month of a "3-6 months" / "6-12 months" style string (legacy fallback support).
export function timeToFirstValueMonths(timeToValue: string): number {
  const n = parseInt(timeToValue, 10);
  return Number.isFinite(n) ? n : 99;
}

/** Resolve the value/feasibility quadrant for an opportunity, preferring the authored use-case dims. */
export function quadrantForBadge(o: BadgeInput): Quadrant | undefined {
  const uc = o.id ? OPP_TO_UC[o.id] : undefined;
  const fromUc = uc != null ? quadrantForUseCase(uc) : undefined;
  if (fromUc) return fromUc;
  if (typeof o.bvAxis === 'number' && typeof o.ieAxis === 'number') return quadrantFromAxes(o.bvAxis, o.ieAxis);
  return undefined;
}

export function isQuickWin(o: BadgeInput): boolean {
  // No quadrant => no Quick Win. The old "Low effort AND time-to-first-value <= 6 months" rule
  // is RETIRED, not merely deprioritised: applying it to fillers with no authored dimensions
  // manufactured Quick Wins the data does not support (it inflated the ranked-list count from
  // the data-derived 8 to 13). A row we cannot place in the quadrant is not a Quick Win.
  return quadrantForBadge(o) === 'Quick Win';
}

export type EffortTier = 'Low' | 'Medium' | 'High';

/**
 * Effort tier DERIVED from the root feasibility axis — Low if feasibility >= 70, Medium if >= 58,
 * else High. This is the badge-derivation rule from the locked root table, and it replaces reading
 * the stored `implementationEffort` string on every SEP/OIR surface that sequences, resources, or
 * labels by effort. Falls back to the stored string only for fillers with no authored dimensions.
 */
export function effortTierForBadge(o: BadgeInput): EffortTier {
  const uc = o.id ? OPP_TO_UC[o.id] : undefined;
  const feasibility = uc != null ? scoreForUseCase(uc)?.impl : (typeof o.ieAxis === 'number' ? o.ieAxis : undefined);
  if (feasibility === undefined) {
    const stored = o.implementationEffort;
    return stored === 'Low' || stored === 'Medium' || stored === 'High' ? stored : 'Medium';
  }
  return feasibility >= 70 ? 'Low' : feasibility >= 58 ? 'Medium' : 'High';
}

/** Quick Win / Moderate / Complex label for a row, from the quadrant (never from stored effort). */
export function effortLabelForBadge(o: BadgeInput): 'Quick Win' | 'Moderate' | 'Complex' {
  if (isQuickWin(o)) return 'Quick Win';
  return effortTierForBadge(o) === 'High' ? 'Complex' : 'Moderate';
}

export function getOpportunityBadge(o: BadgeInput): OpportunityBadge {
  const q = quadrantForBadge(o);
  if (q === 'Quick Win') return 'Quick Win';
  if (q === 'Big Bet') return 'Big Bet';
  if (q === 'Incremental') return 'Incremental';
  if (q === 'Deprioritize') return 'Complex';
  // Legacy fallback for callers still passing only effort/time.
  if (isQuickWin(o)) return 'Quick Win';
  return o.implementationEffort === 'High' ? 'Complex' : 'Moderate';
}
